import { ExpandMore, Visibility } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GridColDef } from '@mui/x-data-grid';
import { CustomDataGrid } from '@shared/components';
import { ElementType, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { accordionStyles } from '../utils/constants';

interface AccordionItemProps<T extends object> {
  id: string;
  title: string;
  icon: ElementType;
  expanded: boolean;
  data: T[];
  columns: GridColDef<T>[];
  visibleColumns: string[];
  getRowId: (row: T) => string | number;
  lastPush: string | null;
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
}

export const PushModuleAccordionItem = <T extends object>({
  id,
  title,
  icon: Icon,
  expanded,
  data,
  columns,
  visibleColumns,
  getRowId,
  lastPush,
  onChange,
}: AccordionItemProps<T>) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const activeCount = useMemo(() => data?.length || 0, [data]);

  const handleViewAllClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/${id}?updatedSince=${lastPush}`);
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      sx={accordionStyles.root}
    >
      <AccordionSummary
        aria-controls={`${id}-content`}
        id={`${id}-header`}
        sx={{
          ...accordionStyles.summary(expanded)(theme),
          '& .MuiAccordionSummary-expandIconWrapper': {
            display: 'none',
          },
          px: 3,
          py: 1,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={2} flex={1}>
            <Icon sx={accordionStyles.icon} />
            <Typography variant="h6" sx={accordionStyles.typography(theme)}>
              {title}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1.5}>
            {/* 🔧 FIX: render as div instead of button */}
            <IconButton
              component="div"
              role="button"
              tabIndex={0}
              size="small"
              sx={{
                border: 1,
                borderColor: 'primary.main',
                borderRadius: 1.5,
                width: 24,
                height: 24,
                '&:hover': { backgroundColor: 'primary.lighter' },
                '& svg': {
                  width: '13px',
                  height: '13px',
                  fill: '#f27013',
                },
              }}
              title="View All Records"
              onClick={handleViewAllClick}
            >
              <Visibility />
            </IconButton>

            <Chip
              label={`${activeCount} active`}
              size="small"
              color="primary"
              variant="outlined"
              sx={accordionStyles.chip}
            />

            {/* 🔧 FIX: also render expand icon button as div */}
            <IconButton
              component="div"
              role="button"
              tabIndex={0}
              size="small"
              sx={{
                ml: 0.5,
                transition: 'transform 0.3s ease',
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              <ExpandMore sx={{ color: 'text.secondary' }} />
            </IconButton>
          </Stack>
        </Stack>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          backgroundColor: theme.palette.background.default,
          pt: 0,
        }}
      >
        <Box sx={{ height: 250, width: '100%', mt: 2 }}>
          {data && data.length > 0 ? (
            <CustomDataGrid
              rows={data}
              columns={columns}
              getRowId={getRowId}
              rowCount={data.length}
              autoHeight={false}
              visibleColumns={visibleColumns}
              sx={{ height: 250 }}
              hideFooter
            />
          ) : (
            <Stack mt={2} alignItems="center">
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 700 }}>
                No Record Available
              </Typography>
            </Stack>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
